import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PipelineSourceField, PipelineStep, StepConfig, VisualPipeline } from './types';
import { FUNCTION_REGISTRY_MAP } from './functionRegistry';
import { parsePipelineFromExpression, serializePipeline } from './expressionSerializer';

let _idCounter = 0;
const makeStepId = (): string => `vs-${++_idCounter}-${Date.now().toString(36)}`;

export interface UseVisualPipelineReturn {
  pipeline: VisualPipeline;
  expression: string;
  addStep: (functionName: string) => void;
  removeStep: (stepId: string) => void;
  updateStepConfig: (stepId: string, config: Partial<StepConfig>) => void;
  reorderSteps: (activeId: string, overId: string) => void;
  setAdvancedMode: (advanced: boolean) => void;
  setExpressionDirectly: (expr: string) => void;
  resetPipeline: () => void;
}

export const useVisualPipeline = (
  initialExpression: string,
  sourceFields: PipelineSourceField[]
): UseVisualPipelineReturn => {
  const initialExprRef = useRef(initialExpression);

  const [pipeline, setPipeline] = useState<VisualPipeline>(() =>
    parsePipelineFromExpression(initialExpression, sourceFields)
  );

  // Re-parse when the initial expression changes externally (e.g., mapping selection changes)
  useEffect(() => {
    if (initialExpression !== initialExprRef.current) {
      initialExprRef.current = initialExpression;
      setPipeline(parsePipelineFromExpression(initialExpression, sourceFields));
    }
  }, [initialExpression, sourceFields]);

  // Keep source fields in sync
  useEffect(() => {
    setPipeline((prev) => {
      if (prev.sourceFields === sourceFields) return prev;
      // Only update source fields if the pipeline currently has none or they differ
      if (prev.sourceFields.length === 0 && sourceFields.length > 0) {
        return { ...prev, sourceFields: [sourceFields[0]] };
      }
      return prev;
    });
  }, [sourceFields]);

  const expression = useMemo(() => {
    if (pipeline.isAdvancedMode) {
      // In advanced mode, expression is managed directly, not from pipeline
      return initialExprRef.current;
    }
    return serializePipeline(pipeline);
  }, [pipeline]);

  const addStep = useCallback((functionName: string) => {
    const entry = FUNCTION_REGISTRY_MAP[functionName];
    if (!entry) return;
    const newStep: PipelineStep = {
      id: makeStepId(),
      functionName,
      config: structuredClone(entry.defaultConfig),
    };
    setPipeline((prev) => ({
      ...prev,
      steps: [...prev.steps, newStep],
    }));
  }, []);

  const removeStep = useCallback((stepId: string) => {
    setPipeline((prev) => ({
      ...prev,
      steps: prev.steps.filter((s) => s.id !== stepId),
    }));
  }, []);

  const updateStepConfig = useCallback((stepId: string, config: Partial<StepConfig>) => {
    setPipeline((prev) => ({
      ...prev,
      steps: prev.steps.map((s) =>
        s.id === stepId ? { ...s, config: { ...s.config, ...config } } : s
      ),
    }));
  }, []);

  const reorderSteps = useCallback((activeId: string, overId: string) => {
    setPipeline((prev) => {
      const oldIndex = prev.steps.findIndex((s) => s.id === activeId);
      const newIndex = prev.steps.findIndex((s) => s.id === overId);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;

      const newSteps = [...prev.steps];
      const [moved] = newSteps.splice(oldIndex, 1);
      newSteps.splice(newIndex, 0, moved);
      return { ...prev, steps: newSteps };
    });
  }, []);

  const setAdvancedMode = useCallback((advanced: boolean) => {
    setPipeline((prev) => ({ ...prev, isAdvancedMode: advanced }));
  }, []);

  const setExpressionDirectly = useCallback(
    (expr: string) => {
      initialExprRef.current = expr;
      // Attempt to re-parse when the user updates the raw expression
      const parsed = parsePipelineFromExpression(expr, sourceFields);
      setPipeline(parsed);
    },
    [sourceFields]
  );

  const resetPipeline = useCallback(() => {
    setPipeline({
      sourceFields: sourceFields.length > 0 ? [sourceFields[0]] : [],
      steps: [],
      isAdvancedMode: false,
    });
    initialExprRef.current = '';
  }, [sourceFields]);

  return {
    pipeline,
    expression,
    addStep,
    removeStep,
    updateStepConfig,
    reorderSteps,
    setAdvancedMode,
    setExpressionDirectly,
    resetPipeline,
  };
};
